import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';

interface ImageUploadModalProps {
  open: boolean;
  onClose: () => void;
  onImageInsert: (imageUrl: string, altText: string) => void;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  open,
  onClose,
  onImageInsert
}) => {
  const [imageUrl, setImageUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Verificar se é uma imagem
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem.');
        return;
      }

      // Verificar tamanho do arquivo (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('O arquivo deve ter no máximo 5MB.');
        return;
      }

      setSelectedFile(file);
      setError('');

      // Criar preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreviewUrl(result);
        setImageUrl(result); // Para inserção direta
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setImageUrl(url);
    if (url) {
      setPreviewUrl(url);
      setSelectedFile(null);
    }
  };

  const handleInsert = () => {
    if (!imageUrl) {
      setError('Por favor, selecione uma imagem ou insira uma URL.');
      return;
    }

    onImageInsert(imageUrl, altText || 'Imagem');
    handleClose();
  };

  const handleClose = () => {
    setImageUrl('');
    setAltText('');
    setSelectedFile(null);
    setPreviewUrl('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ zIndex: 10000 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Inserir Imagem
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Upload de arquivo */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Fazer upload de arquivo:
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              fullWidth
              sx={{ mb: 1 }}
            >
              Selecionar Imagem
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
            {selectedFile && (
              <Typography variant="caption" color="textSecondary">
                Arquivo selecionado: {selectedFile.name}
              </Typography>
            )}
          </Box>

          {/* Ou inserir URL */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Ou inserir URL da imagem:
            </Typography>
            <TextField
              fullWidth
              label="URL da Imagem"
              value={imageUrl}
              onChange={handleUrlChange}
              placeholder="https://exemplo.com/imagem.jpg"
            />
          </Box>

          {/* Texto alternativo */}
          <TextField
            fullWidth
            label="Texto Alternativo (Alt)"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            placeholder="Descrição da imagem"
            helperText="Texto que aparece quando a imagem não carrega"
          />

          {/* Preview da imagem */}
          {previewUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview:
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  textAlign: 'center',
                  maxHeight: 200,
                  overflow: 'hidden'
                }}
              >
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '180px',
                    objectFit: 'contain'
                  }}
                  onError={() => setError('Erro ao carregar a imagem. Verifique a URL.')}
                />
              </Box>
            </Box>
          )}

          {/* Mensagem de erro */}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleInsert}
          variant="contained"
          disabled={!imageUrl}
        >
          Inserir Imagem
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUploadModal; 